<template>
  <div class="container py-3">
    <div class="page-header">
      <h2>{{ $t('supplier.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">{{ $t('supplier.add') }}</button>
    </div>

    <!-- Search -->
    <div class="search-wrapper mb-3">
      <span class="search-icon">🔍</span>
      <input v-model="search" type="text" class="form-control" :placeholder="$t('supplier.searchPlaceholder')" />
    </div>

    <!-- Table -->
    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('common.name') }}</th>
            <th>{{ $t('common.email') }}</th>
            <th>{{ $t('common.phone') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="suppliers.length === 0">
            <td colspan="4"><div class="empty-state"><div class="empty-icon">🏭</div><p>No suppliers found</p></div></td>
          </tr>
          <tr v-for="supplier in suppliers" :key="supplier.id">
            <td><strong>{{ supplier.name }}</strong></td>
            <td>{{ supplier.email }}</td>
            <td>{{ supplier.phone }}</td>
            <td>
              <button class="btn-action btn-action-warning" @click="openEditModal(supplier)">✏️ {{ $t('common.edit') }}</button>
              <button class="btn-action btn-action-danger" @click="deleteSupplier(supplier.id)">🗑️ {{ $t('common.delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
    />

    <!-- Add/Edit Modal -->
    <div class="modal fade" ref="supplierModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveSupplier">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? $t('supplier.edit') : $t('supplier.add') }}</h5>
              <button type="button" class="btn-close" @click="hideModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">{{ $t('common.name') }}</label>
                <input v-model="form.name" type="text" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('common.email') }}</label>
                <input v-model="form.email" type="email" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('common.phone') }}</label>
                <input v-model="form.phone" type="text" class="form-control" />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="hideModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ isEditing ? $t('common.update') : $t('common.add') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '../api/axios'
import * as bootstrap from 'bootstrap'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


const suppliers = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)
const form = ref({})
const isEditing = ref(false)

const supplierModal = ref(null)
let supplierModalInstance = null

const fetchSuppliers = async () => {
  try {
    const res = await api.get('/suppliers/', {
      params: {
        page: currentPage.value,
        page_size: pageSize.value,
        search: search.value
      }
    })
    suppliers.value = res.data.results
    totalRecords.value = res.data.count
  } catch (err) {
    console.error(err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchSuppliers()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchSuppliers()
})

onMounted(() => {
  fetchSuppliers()
  supplierModalInstance = new bootstrap.Modal(supplierModal.value)
})

const openAddModal = () => {
  isEditing.value = false
  form.value = { name: '', email: '', phone: '' }
  supplierModalInstance.show()
}

const openEditModal = (supplier) => {
  isEditing.value = true
  form.value = { ...supplier }
  supplierModalInstance.show()
}

const hideModal = () => supplierModalInstance.hide()

const saveSupplier = async () => {
  try {
    if (isEditing.value) {
      await api.put(`/suppliers/${form.value.id}/`, form.value)
    } else {
      await api.post('/suppliers/', form.value)
    }
    fetchSuppliers()
    hideModal()
  } catch (err) {
    console.error(err)
  }
}

const deleteSupplier = async (id) => {
  if (!confirm(t('supplier.confirmDelete'))) return
  try {
    await api.delete(`/suppliers/${id}/`)
    fetchSuppliers()
  } catch (err) {
    console.error(err)
  }
}
</script>
